import React from 'react';
import { Chip } from '@mui/material';
import {
	getGridSingleSelectOperators,
	GridCellParams,
	GridColDef,
	GridColumns,
	GridFilterItem,
	GridRenderCellParams,
	GridValueFormatterParams,
} from '@mui/x-data-grid-pro';
import {
	BulkInventoryItem,
	getInventoryFieldsArray,
	InventoryFields,
	InventoryField,
	SingleInventoryItem,
	InventoryItemAllocationQuantity,
	InventoryItemAllocationBoolean,
	isInventoryItemAllocationBoolean,
	isInventoryItemAllocationQuantity,
} from 'common/modules/inventory';
import { EnumKeys, PurchaseTypes } from 'common/types';
import { getTypedKeys, stringEnum } from 'common/utils/objects';
import { NO_SKU_CODE } from './rows';
import type { TFunction } from 'i18next';
import {
	getInventoryAllocationOptionTranslation,
	getInventoryStateTranslation,
	localizedType,
} from 'views/Inventory/utils';
import { StatusCell } from 'views/Inventory/components/StatusCell';
import GridCellWithTooltip from 'views/Inventory/components/GridCellWithTooltip';
import { formatTime } from 'common/utils/dateUtils';
import { InventoryStatuses } from 'common/modules/inventory/constants';
import { switchUnreachable } from 'common/utils/common';
import { arrayIncludes } from 'common/utils/arrays';
import { AllocationCell } from 'views/Inventory/components/AllocationCell';

export const inventoryStatusList = getTypedKeys(InventoryStatuses);
export const inventoryAllocationsList = getTypedKeys(PurchaseTypes);

export const FixedArticleColumns = stringEnum([
	'id',
	'article_code',
	'sku_code',
	'sku_name',
	'store',
	'status',
	'state',
	'allocations',
	'quantity',
	'usageCount',
	'usageHours',
	'type',
	'sole_length',
	'din_variation',
]);

const {
	id,
	article_code,
	sku_code,
	sku_name,
	store,
	status,
	state,
	allocations,
	quantity,
	usageCount,
	usageHours,
	type,
	sole_length,
	din_variation,
} = FixedArticleColumns;

export interface ColumnFieldsWithTypes
	extends Record<FixedArticleColumnField, string | undefined | null | number | string[] | object> {
	[sku_code]: string | null;
	[sku_name]: string;
	[store]: string;
	[article_code]: string[];
	[status]: string | BulkInventoryItem['statuses'];
	[allocations]: InventoryItemAllocationBoolean | InventoryItemAllocationQuantity;
	[state]: string;
	[quantity]: number;
	[usageCount]: number;
	[usageHours]: number;
	[type]: 'single' | 'bulk';
}

export type FixedArticleColumnField = EnumKeys<typeof FixedArticleColumns>;

interface ColumnParams extends GridColDef {
	field: FixedArticleColumnField;
}

export const getColumnName = (label: string, t: TFunction) => {
	const columnNameMapping: { [key in FixedArticleColumnField]: string } = {
		[id]: 'Internal ID',
		[article_code]: t('inventory.columns.articleId', 'Article Code'),
		[sku_code]: t('inventory.columns.skuCOde', 'SKU Code'),
		[sku_name]: t('inventory.columns.skuName', 'SKU Name'),
		[store]: t('inventory.columns.store', 'Store'),
		[status]: t('inventory.columns.status', 'Status'),
		[state]: t('inventory.columns.state', 'Current state'),
		[allocations]: t('inventory.columns.allocation', 'Allocation'),
		[quantity]: t('inventory.columns.quantity', 'Quantity'),
		[usageCount]: t('inventory.columns.usageCount', 'Times used'),
		[usageHours]: t('inventory.columns.usageHours', 'Hours used'),
		[type]: t('inventory.columns.type', 'Type'),
		[sole_length]: t('inventory.fixedFieldValues.soleLength', 'Sole length'),
		[din_variation]: t('inventory.fixedFieldValues.dinVariation', 'DIN variation'),
	};
	return columnNameMapping[label] ?? label;
};

const renderCellWithOverflowTooltip = (params: GridRenderCellParams<string>) => (
	<GridCellWithTooltip
		value={params.value || ''}
		tooltipValue={params.value || ''}
		width={params.colDef.computedWidth}
		tooltipOnHover="ifOverflow"
		cellOverflow="ellipsis"
	/>
);

const getBaseColumnInfo = (
	field: FixedArticleColumnField,
	t: TFunction,
): Pick<ColumnParams, 'field' | 'headerName'> => {
	return {
		field,
		headerName: getColumnName(field, t),
	};
};

type SingleSelectOperator = 'is' | 'not';
const expectedFilterOperations: SingleSelectOperator[] = ['is', 'not'];

const customStatusFilterSingleSelectApplyFn = (filterItem: GridFilterItem) => {
	const operatorValue = filterItem.operatorValue;
	if (!filterItem.columnField || !filterItem.value || !operatorValue) {
		return null;
	}
	if (!arrayIncludes(expectedFilterOperations, operatorValue)) {
		return null;
	}
	return (params: GridCellParams) => {
		const value = params.value as SingleInventoryItem['status'] | BulkInventoryItem['statuses'];
		switch (operatorValue) {
			case 'is': {
				if (typeof value === 'string') return filterItem.value === params.value;
				return params.value[filterItem.value] != null;
			}
			case 'not': {
				if (typeof value === 'string') return filterItem.value !== params.value;
				return params.value[filterItem.value] == null;
			}
			default:
				return switchUnreachable(operatorValue);
		}
	};
};

const customAllocationFilterSingleSelectApplyFn = (filterItem: GridFilterItem) => {
	const operatorValue = filterItem.operatorValue;
	if (!filterItem.columnField || !filterItem.value || !operatorValue) {
		return null;
	}
	if (!arrayIncludes(expectedFilterOperations, operatorValue)) {
		return null;
	}
	return (params: GridCellParams) => {
		switch (operatorValue) {
			case 'is': {
				return params.value[filterItem.value] === true || params.value[filterItem.value] > 0;
			}
			case 'not': {
				return !params.value[filterItem.value];
			}
			default:
				return switchUnreachable(operatorValue);
		}
	};
};

export const getFixedColumns = (opts: {
	t: TFunction;
	multiLocation: boolean;
	showDinVariation: boolean;
	showSoleLength: boolean;
}): (ColumnParams | undefined)[] => {
	const { t, multiLocation, showDinVariation, showSoleLength } = opts;
	return [
		{
			...getBaseColumnInfo(id, t),
			hide: true,
		},
		{
			...getBaseColumnInfo(article_code, t),
			width: 150,
			valueGetter: (params) => {
				return (params.row[params.field] as string[]).join(', ');
			},
			sortComparator: (v1, v2) => {
				if (v1 === '') return 1;
				if (v2 === '') return -1;
				if (typeof v1 !== 'string' || typeof v2 !== 'string') return 0;
				const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
				const firstA = v1
					.split(',')
					.map((v) => v.trim())
					.sort(collator.compare)[0];
				const firstB = v2
					.split(',')
					.map((v) => v.trim())
					.sort(collator.compare)[0];
				return collator.compare(firstA, firstB);
			},
		},
		{
			...getBaseColumnInfo(sku_code, t),
			flex: 1,
			minWidth: 130,
			cellClassName: (params: GridCellParams) => {
				return params.value == null ? 'no-sku-cell' : '';
			},
			renderCell: (params) => params.value ?? NO_SKU_CODE,
		},
		{
			...getBaseColumnInfo(sku_name, t),
			flex: 1,
			minWidth: 130,
			renderCell: renderCellWithOverflowTooltip,
		},
		multiLocation
			? {
					...getBaseColumnInfo(store, t),
					flex: 1,
					minWidth: 100,
			  }
			: undefined,
		{
			...getBaseColumnInfo(status, t),
			valueFormatter: (params: GridValueFormatterParams) => {
				const value = params.value as SingleInventoryItem['status'] | BulkInventoryItem['statuses'];
				if (typeof value === 'string') return value;
				return Object.entries(value)
					.map(([status, qty]) => `${status}:${qty}`)
					.join(',');
			},
			renderCell: (
				params: GridRenderCellParams<SingleInventoryItem['status'] | BulkInventoryItem['statuses']>,
			) => <StatusCell value={params.value ?? {}} />,
			cellClassName: 'overflow-cell',
			flex: 1,
			minWidth: 150,
			sortable: false,
			type: 'singleSelect',
			valueOptions: inventoryStatusList,
			filterOperators: getGridSingleSelectOperators()
				.filter((op) => expectedFilterOperations.includes(op.value as any))
				.map((op) => ({
					...op,
					getApplyFilterFn: customStatusFilterSingleSelectApplyFn,
				})),
		},
		{
			...getBaseColumnInfo(allocations, t),
			description: t('inventory.allocationsDescription', 'Allocations of the article'),
			valueFormatter: (params: GridValueFormatterParams) => {
				const value = params.value as
					| InventoryItemAllocationBoolean
					| InventoryItemAllocationQuantity;

				if (isInventoryItemAllocationBoolean(value)) {
					return value.rental ? PurchaseTypes.rental : value.sales ? PurchaseTypes.sales : '';
				}
				if (isInventoryItemAllocationQuantity(value)) {
					return Object.entries(value)
						.map(([type, amount]) => {
							return `${type}:${amount ?? 0}`;
						})
						.join(',');
				}

				return '';
			},
			cellClassName: 'overflow-cell',
			renderCell: (
				params: GridRenderCellParams<
					InventoryItemAllocationBoolean | InventoryItemAllocationQuantity
				>,
			) => <AllocationCell value={params.value ?? {}} />,
			flex: 1,
			minWidth: 150,
			sortable: false,
			type: 'singleSelect',
			valueOptions: inventoryAllocationsList.map((value) => ({
				value,
				label: getInventoryAllocationOptionTranslation(t, value),
			})),
			filterOperators: getGridSingleSelectOperators()
				.filter((op) => expectedFilterOperations.includes(op.value as any))
				.map((op) => ({
					...op,
					getApplyFilterFn: customAllocationFilterSingleSelectApplyFn,
				})),
		},
		{
			...getBaseColumnInfo(usageCount, t),
			type: 'number',
			flex: 1,
			minWidth: 100,
		},
		{
			...getBaseColumnInfo(usageHours, t),
			type: 'number',
			flex: 1,
			minWidth: 100,
			valueFormatter: (params: GridValueFormatterParams) => {
				const value = params.value as number;
				return formatTime(value, { precision: 1, suffix: 'h' });
			},
		},
		{
			...getBaseColumnInfo(type, t),
			flex: 1,
			minWidth: 100,
			type: 'singleSelect',
			valueOptions: ['single', 'bulk'],
			renderCell: (params: GridCellParams) => {
				return localizedType(params.value, t);
			},
		},
		{
			...getBaseColumnInfo(quantity, t),
			type: 'number',
			valueFormatter: ({ value }) => value,
			flex: 1,
			minWidth: 100,
		},
		{
			...getBaseColumnInfo(state, t),
			description: t(
				'inventory.stateDescription',
				'If article is currently in inventory or part of an active order',
			),
			valueFormatter: (params: GridValueFormatterParams) =>
				getInventoryStateTranslation(params.value as 'IN' | 'OUT'),
			renderCell: (params: GridRenderCellParams<'IN' | 'OUT'>) => (
				<Chip
					size="small"
					variant="outlined"
					label={params.value ? getInventoryStateTranslation(params.value) : ''}
					sx={{ minWidth: 40 }}
				/>
			),
			type: 'singleSelect',
			valueOptions: ['IN', 'OUT'],
			flex: 1,
			minWidth: 100,
		},
		showSoleLength
			? {
					...getBaseColumnInfo(sole_length, t),
					flex: 1,
					type: 'number',
					minWidth: 100,
			  }
			: undefined,
		showDinVariation
			? {
					...getBaseColumnInfo(din_variation, t),
					flex: 1,
					type: 'number',
					minWidth: 100,
			  }
			: undefined,
	];
};

export const getDynamicColumns = (inventoryFields: InventoryFields): GridColumns => {
	return getInventoryFieldsArray(inventoryFields)?.map((field: InventoryField) => ({
		field: field.id,
		// Inventory export cannot handle empty column field names, so we overwrite empty header name to single space
		headerName: field.label === '' ? ' ' : field.label,
		flex: 1,
		minWidth: 100,
		type: field.type === 'number' ? 'number' : 'string',
		renderCell: renderCellWithOverflowTooltip,
	}));
};
